import { useMutation, useQueryClient } from '@tanstack/react-query';
import { postText, ApiError } from '@/shared/api/api-client';
import type { UnblockSlotRequest } from '@/types/block-availability-api';
import { APPOINTMENT_LIST_QUERIES } from './appointment-list-keys';

/**
 * POST /api/Scheduling/UnblockSlot
 *
 * Wire shape verified via dev curl 2026-05-19 — 3 keys exactly:
 *   { id, practiceId, recurringId }
 *
 * Mirrors Flutter `scheduling_data_source_impl.dart:497-509`. Same
 * `accept: text/plain` content-type as `BlockSlot` — response body
 * is a plain "" string on success.
 *
 * `recurringId` semantics:
 *   - `null` → unblock just this single occurrence.
 *   - `<recurring.id>` → unblock the recurring series (this + all
 *     following).
 *
 * Success invalidates appointment queries so the freed slot renders
 * back on the calendar. Same key set as `useSaveBlockSlot`.
 */
/**
 * The one unblock POST both routes run — the client's `UnblockSlotCore`
 * (PROD-2510). A plain function rather than a shared hook, for the same reason
 * `postBlockSlotTo` is: nothing stateful to share, and an exported hook nobody
 * should call directly is a footgun beside the two that should be.
 */
export async function postUnblockSlotTo(route: string, body: UnblockSlotRequest): Promise<void> {
  await postText(route, body);
}

/**
 * What an unblock invalidates, for either actor. Stated once so a device
 * unblock cannot quietly refresh a narrower set than a practitioner one and
 * leave a freed window still painted busy on the other tab.
 */
export function invalidateAfterUnblock(
  qc: ReturnType<typeof useQueryClient>,
  blockId: string,
): void {
  void qc.invalidateQueries(APPOINTMENT_LIST_QUERIES);
  void qc.invalidateQueries({ queryKey: ['day-list-appointments'] });
  void qc.invalidateQueries({ queryKey: ['month-appointments'] });
  // Drop the cached block-appointment record so any re-open
  // doesn't show stale data. BOTH hydration keys — PROD-2512 gave the device
  // its own route and so its own bucket — for the reason stated above: one
  // actor kind must not refresh a narrower set than the other.
  void qc.invalidateQueries({ queryKey: ['block-appointment', blockId] });
  void qc.invalidateQueries({ queryKey: ['block-resource-appointment', blockId] });
}

/** The PRACTITIONER unblock route. A device-owned slot sent here is refused 400. */
export function useUnblockSlot() {
  const qc = useQueryClient();
  return useMutation<void, ApiError, UnblockSlotRequest>({
    mutationKey: ['unblock-slot'],
    mutationFn: (body) => postUnblockSlotTo('/api/Scheduling/UnblockSlot', body),
    onSuccess: (_data, vars) => invalidateAfterUnblock(qc, vars.id),
  });
}
